// src/pages/StudentEntryForm.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import StudentDetails from '../components/StudentForm/StudentDetails';
import CourseDetails from '../components/StudentForm/CourseDetails';
import PerformanceSection from '../components/StudentForm/PerformanceSection';
import ProjectsSection from '../components/StudentForm/ProjectsSection';
import { useToast } from '../components/Admissionform/ToastManager';
import { RegDataContext } from '../context/RegDataContext';

import '../styles/StudentEntryForm.css';

const StudentEntryForm = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useToast();
  const token = localStorage.getItem('token');
  const empName = localStorage.getItem('emp_name');


  const {
    locationsData,
    coursesData,
    courseFeesData,
    courseDurData,
    trainers,
    fetchTrainers
  } = useContext(RegDataContext);

  const [activeTab, setActiveTab] = useState('studentCourse');
  const [loading, setLoading] = useState(false);
  const [isGeneratingId, setIsGeneratingId] = useState(false);

  const [formData, setFormData] = useState({
    studentRegisterNumber: '',
    name: '',
    contactNo: '',
    email: '',
    courseType: '',
    courseName: '',
    courseFees: '',
    courseDuration: '',
    studentProgressStatus: 'Course In Progress',
    location: '',
    branch: '',
    staffAssigned: '',
    mentorName: '',
    staffAssignedId: '',
    mentorId: '',
    mentorNumber: '',
    technicalScore: 0,
    technologyRemarks: '',
    communicationScore: 0,
    communicationRemarks: '',
    projects: [{ projectTitle: '', projectStatus: '', projectScore: 0 }],
    mockInterview: 'No',
    mockRemarks: '',
    progressFeedback: '',
    department: '',
    skillsKnown: [],
    gender: '',
    readyForPlacement: '',
    placementStatus: 'Awaiting for placement',
    resumePath: '',

  });

  // --- Abbreviations for ID generation ---
  const COURSE_TYPE_ABBREVIATIONS = {
    "CADD": "CD",
    "Pumo Tech IT": "PI",
    "Pumo Tech Automation": "PA",
    "Monz Creative School": "MZ",
  };

  const BRANCH_ABBREVIATIONS = {
    "Tambaram": "TM",
    "Velachery": "VL",
    "Vadapalani": "VP",
    "Poonamallee": "PM",
    "Marathahalli": "MH",
    "Gandhipuram": "GP",
    "Malumichampatti": "MP",
    "Hosur": "HS",
    "Saravanampatti": "SP",
    "Tiruppur": "TP",
    "Padi": "PD",
  };

  const getCourseTypeAbbreviation = (courseType) => {
    if (!courseType) return "CR";
    return COURSE_TYPE_ABBREVIATIONS[courseType] || courseType.substring(0, 2).toUpperCase();
  };

  const getBranchAbbreviation = (branchName) => {
    if (!branchName) return "UNK";
    return BRANCH_ABBREVIATIONS[branchName] || branchName.substring(0, 2).toUpperCase();
  };

  // --- Auto-select location/branch if only one ---
  useEffect(() => {
    const locKeys = Object.keys(locationsData);
    if (locKeys.length === 1) {
      setFormData(prev => ({ ...prev, location: locKeys[0] }));
    }
  }, [locationsData]);

  useEffect(() => {
    if (formData.location && locationsData[formData.location]?.branches.length === 1) {
      setFormData(prev => ({ ...prev, branch: locationsData[formData.location].branches[0] }));
    }
  }, [formData.location, locationsData]);

  // --- Generate next student ID ---
  const fetchNextId = async () => {
    if (!formData.branch || !formData.courseType) return;

    setIsGeneratingId(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/registrations`,
        { adminbranch: formData.branch, courseType: formData.courseType },
        { params: { preview: 'true' }, headers: { Authorization: `Bearer ${token}` } }
      );

      setFormData(prev => ({ ...prev, studentRegisterNumber: res.data.nextId }));
    } catch (err) {
      const courseAbbrev = getCourseTypeAbbreviation(formData.courseType);
      const branchAbbrev = getBranchAbbreviation(formData.branch);
      setFormData(prev => ({ ...prev, studentRegisterNumber: `${courseAbbrev}-${branchAbbrev}-XXXX` }));
    } finally {
      setIsGeneratingId(false);
    }
  };

  useEffect(() => {
    if (formData.courseType && formData.branch) fetchNextId();
  }, [formData.courseType, formData.branch]);

  // --- Fetch trainers when location/branch changes ---
  useEffect(() => {
    if (formData.location && formData.branch) fetchTrainers(formData.location, formData.branch);
  }, [formData.location, formData.branch]);

  // --- Department & skills handlers ---
  const handleDepartmentChange = (selectedDept) => {
    setFormData(prev => ({ ...prev, department: selectedDept }));
  };

  const handleSkillsChange = (selectedSkills) => {
    setFormData(prev => ({ ...prev, skillsKnown: selectedSkills }));
  };

  const handleProjectsChange = (updatedProjects) => {
    setFormData(prev => ({ ...prev, projects: updatedProjects }));
  };

  // --- Submit handler ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!token) {
        showWarning("Please log in again.");
        setLoading(false);
        return;
      }
      if (empName) {
        const isStaffAssigned = formData.staffAssigned === empName;
        const isMentor = formData.mentorName === empName;

        if (!isStaffAssigned && !isMentor) {
          showWarning("You cannot create a student. Assigned Staff or Mentor must be you.");
          setLoading(false);
          return;
        }
      }
      if (!formData.skillsKnown || formData.skillsKnown.length === 0) {
        showWarning("Please select at least one Skill Known.");
        setLoading(false);
        return;
      }
      if (!formData.contactNo || formData.contactNo.length !== 10) {
        showWarning("Please enter a valid 10-digit contact number");
        setLoading(false);
        return;
      }
      if (formData.readyForPlacement === 'Yes' && !formData.resumePath) {
        showWarning("Please upload a resume before saving. Resume is required when student is ready for placement.");
        setLoading(false);
        return;
      }
      // Ensure branch fields are strings
      const branchValue = Array.isArray(formData.branch) ? formData.branch[0] : formData.branch || "";
      const locationValue = Array.isArray(formData.location) ? formData.location[0] : formData.location || "";

      const registrationData = {
        studentRegisterNumber: formData.studentRegisterNumber,
        name: formData.name,
        contactNo: formData.contactNo,
        email_Id: formData.email,
        courseType: formData.courseType,
        course: formData.courseName,
        courseFees: formData.courseFees,
        courseDuration: formData.courseDuration,
        studentProgressStatus: formData.studentProgressStatus,
        adminbranch: branchValue,
        adminlocation: locationValue,
        studentRequestedBranch: branchValue,
        studentRequestedLocation: locationValue,
        staffAssigned: formData.staffAssigned,
        department: formData.department,
        resumePath: formData.resumePath,
      };

      if (!registrationData.contactNo || !registrationData.course || !registrationData.adminbranch) {
        showWarning("Fill required fields: Contact Number, Course, Branch");
        setLoading(false);
        return;
      }

      // Register student
      const registrationRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/registrations`,
        registrationData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const generatedId = registrationRes.data.student?.studentId || formData.studentRegisterNumber;

      // Build student record data
      const studentRecordData = {
        studentId: generatedId,
        studentName: formData.name,
        studentContactNumber: formData.contactNo,
        courseType: formData.courseType,
        courseName: formData.courseName,
        staffId1: formData.staffAssignedId?.toString() || "",
        staffName1: formData.staffAssigned || "",
        branch: branchValue,
        ProgressStatus: formData.studentProgressStatus,
        email_Id: formData.email,
        mentor: formData.mentorName,
        mentorid: formData.mentorId?.toString() || "",
        mentorNumber: formData.mentorNumber || "",
        technicalScore: formData.technicalScore,
        technologyRemarks: formData.technologyRemarks,
        communicationScore: formData.communicationScore,
        communicationRemarks: formData.communicationRemarks,
        projectTitle1: formData.projects[0]?.projectTitle || '',
        project1Status: formData.projects[0]?.projectStatus || '',
        project1Score: formData.projects[0]?.projectScore || 0,
        projectTitle2: formData.projects[1]?.projectTitle || '',
        project2Status: formData.projects[1]?.projectStatus || '',
        projectScore2: formData.projects[1]?.projectScore || 0,
        projectTitle3: formData.projects[2]?.projectTitle || '',
        project3Status: formData.projects[2]?.projectStatus || '',
        projectScore3: formData.projects[2]?.projectScore || 0,
        Department: formData.department,
        knownSkill: Array.isArray(formData.skillsKnown) ? formData.skillsKnown.join(', ') : formData.skillsKnown,
        mockInterview: formData.mockInterview,
        mockRemarks: formData.mockRemarks,
        progressFeedback: formData.progressFeedback,
        gender: formData.gender,
        readyForPlacement: formData.readyForPlacement,
        placementStatus: formData.placementStatus,

      };

      const studentRecordRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/student-record`,
        studentRecordData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const createdRecord = studentRecordRes.data.data;
      if (!createdRecord?.id) {
        showError('Student record ID missing.');
        setLoading(false);
        return;
      }
      if (formData.readyForPlacement === 'Yes' && formData.resumeFile) {
        const resumeFormData = new FormData();
        resumeFormData.append('resume', formData.resumeFile);
        resumeFormData.append('studentId', generatedId);

        await axios.post(
          `${process.env.REACT_APP_API_URL}/upload-resume`,
          resumeFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      showSuccess('Student entry created successfully!');
      navigate(`/edit-student/${createdRecord.id}`);
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.message || err.message || "Error submitting student entry");
    } finally {
      setLoading(false);
    }
  };


  const handleCancel = () => navigate('/staff-dashboard');

  const showToast = (msg, type) => alert(`${type.toUpperCase()}: ${msg}`);

  return (
    <div className="container">
      <div className="pagesf-wrapper">

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button type="button" className={`tab-button ${activeTab === 'studentCourse' ? 'active' : ''}`} onClick={() => setActiveTab('studentCourse')}>Student & Course Details</button>
          <button type="button" className={`tab-button ${activeTab === 'performance' ? 'active' : ''}`} onClick={() => setActiveTab('performance')}>Performance</button>
          <button type="button" className={`tab-button ${activeTab === 'qualification' ? 'active' : ''}`} onClick={() => setActiveTab('qualification')}>Projects</button>
        </div>

        <form onSubmit={handleSubmit} className="form">
          <div className="tab-content">
            {activeTab === 'studentCourse' && (
              <div className="tab-panel">
                <StudentDetails
                  formData={formData}
                  onFormDataChange={setFormData}
                  onDepartmentChange={handleDepartmentChange}
                  onSkillsChange={handleSkillsChange}
                  locationsData={locationsData}
                  trainers={trainers}
                />
                <CourseDetails
                  formData={formData}
                  onFormDataChange={setFormData}
                  coursesData={coursesData}
                  courseFeesData={courseFeesData}
                  courseDurData={courseDurData}
                />
              </div>
            )}

            {activeTab === 'performance' && (
              <PerformanceSection formData={formData} onFormDataChange={setFormData} showToast={showToast} />
            )}

            {activeTab === 'qualification' && (
              <ProjectsSection formData={formData} onProjectsChange={handleProjectsChange} />
            )}
          </div>

          <div className="form-actions">
            <button type="button" onClick={handleCancel} className="cancel-btn">Cancel</button>
            <button type="submit" disabled={loading} className="submit-btn">{loading ? 'Submitting...' : 'Save'}</button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default StudentEntryForm;
